import { useEffect, useEffectEvent, useMemo } from "react";
import type { Group } from "../../../../../../../features/questionnaire/types";
import { useZipCodeValidation } from "../ZipCodeQuestion/useZipCodeValidation";
import type { Answer, QuestionProps } from "../../../types";

type UseAddressZipCodeValidationParams = {
  group: Group | undefined;
  questions: {
    filter: { getByName: (name: string) => QuestionProps | undefined };
  };
  answers: Record<string, Answer>;
  USStateAnswer: string | undefined;
  setAsyncError: (name: string, message: string) => void;
  clearAsyncError: (name: string) => void;
};

/**
 * Hook to validate zip code in an address lookup group, even when the field is hidden.
 * Manages async validation errors for the zip code question.
 */
export const useAddressZipCodeValidation = ({
  group,
  questions,
  answers,
  USStateAnswer,
  setAsyncError,
  clearAsyncError,
}: UseAddressZipCodeValidationParams) => {
  // Find the zip code question in the group
  const zipCodeQuestion = useMemo(
    () =>
      group?.names
        .map(name => questions.filter.getByName(name))
        .find((q): q is QuestionProps => q?.display?.type === "zipcode"),
    [group?.names, questions],
  );

  const zipCodeAnswer = zipCodeQuestion
    ? answers[zipCodeQuestion.name]
    : undefined;

  // Cast to the expected type (zip code is always a string)
  const zipCodeValue = zipCodeAnswer as string | undefined;

  // Use zip code validation hook - this runs even when ZipCodeQuestion is hidden
  const zipCodeValidation = useZipCodeValidation({
    value: zipCodeValue,
    state: USStateAnswer,
  });

  // Stable callback for managing async errors
  const updateZipCodeError = useEffectEvent(() => {
    if (!zipCodeQuestion || !zipCodeValue || zipCodeValidation.isPending) {
      return;
    }

    if (!zipCodeValidation.isValid && zipCodeValidation.validationMessage) {
      setAsyncError(zipCodeQuestion.name, zipCodeValidation.validationMessage);
      return;
    }

    if (zipCodeValidation.isValid) {
      clearAsyncError(zipCodeQuestion.name);
    }
  });

  // Manage zip code error state based on validation result
  useEffect(() => {
    updateZipCodeError();
  }, [
    zipCodeQuestion,
    zipCodeValue,
    zipCodeValidation.isPending,
    zipCodeValidation.isValid,
    zipCodeValidation.validationMessage,
  ]);
};
